import { useMemo, useState } from "react";
import {
  amsterdamDate,
  type BuildingDto,
  type VisitDto,
  type VisitorInput,
  type VisitSettingsDto,
} from "@klantpunt/shared";
import { Combobox, type ComboItem } from "../../app/Combobox";
import { Modal } from "../../app/Modal";
import { api } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { useDirectoryPick } from "../directory/Pick";
import { errMsg, useVisitPeople, useVisitsRefresh } from "./useVisits";

const pad = (n: number) => String(n).padStart(2, "0");
/** Datum en tijd voor `<input type="date">` en `type="time"` in de tijd van de browser. */
const localDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localTime = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const toIso = (date: string, time: string) => new Date(`${date}T${time}`).toISOString();

/** Ruimtes van het gebouw als keuzelijst, per verdieping. */
export function roomItems(b: BuildingDto): ComboItem[] {
  const floors = new Map(b.floors.map((f) => [f.id, f.name]));
  return b.rooms.map((r) => ({ value: r.id, label: r.name, group: floors.get(r.floorId) ?? "Verdieping" }));
}

type Who = { kind: "member"; userId: string } | { kind: "person"; personId: string } | { kind: "guest" };

/**
 * Bezoek aankondigen (docs/BEZOEK.md): een collega (eigen dienst), iemand uit het adresboek of een nieuwe gast.
 * De gastheer die zelf aankondigt en eigen dienst zijn meteen goedgekeurd; de rest gaat langs een gastheer.
 */
export function AnnounceDialog({
  building: b,
  settings,
  day,
  onClose,
  onSaved,
}: {
  building: BuildingDto;
  settings: VisitSettingsDto;
  day: string;
  onClose: () => void;
  onSaved: (v: VisitDto) => void;
}) {
  const toast = useToast();
  const { data: me } = useMe();
  const refresh = useVisitsRefresh(b.id);
  const people = useVisitPeople(b.id);
  const directory = useDirectoryPick({ buildingId: b.id }, hasModule(me, "directory"));
  const now = new Date();
  const isToday = day === amsterdamDate(now);
  const [who, setWho] = useState<Who | null>(null);
  const [guest, setGuest] = useState({ name: "", company: "", email: "", phone: "" });
  const [date, setDate] = useState(day);
  const [from, setFrom] = useState(
    isToday ? localTime(new Date(Math.ceil(now.getTime() / 900_000) * 900_000)) : "09:00",
  );
  const [until, setUntil] = useState("");
  const [purpose, setPurpose] = useState("");
  const [room, setRoom] = useState<string | null>(null);
  const [host, setHost] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const items = useMemo<ComboItem[]>(() => {
    const out: ComboItem[] = (people.data ?? []).map((p) => ({
      value: `u:${p.id}`,
      label: p.name,
      group: "Collega's",
    }));
    for (const c of directory.data ?? [])
      if (!c.isInternal)
        for (const p of c.people) out.push({ value: `p:${p.id}`, label: p.name, meta: c.name, group: "Adresboek" });
    if (who?.kind === "guest" && guest.name) out.push({ value: "guest", label: guest.name, group: "Nieuwe gast" });
    return out;
  }, [people.data, directory.data, who, guest.name]);
  const whoValue = !who
    ? null
    : who.kind === "member"
      ? `u:${who.userId}`
      : who.kind === "person"
        ? `p:${who.personId}`
        : "guest";
  const hostItems: ComboItem[] = settings.hosts.map((h) => ({ value: h.id, label: h.name }));
  const badEnd = !!until && until <= from;
  const ok = !!who && (who.kind !== "guest" || guest.name.trim() !== "") && !!date && !!from && !badEnd;

  async function save() {
    if (!who) return;
    setBusy(true);
    const visitor: VisitorInput =
      who.kind === "guest"
        ? { kind: "guest", name: guest.name, company: guest.company, email: guest.email, phone: guest.phone }
        : who;
    try {
      const v = await api<VisitDto>(`/buildings/${b.id}/visits`, {
        method: "POST",
        body: {
          visitor,
          startsAt: toIso(date, from),
          endsAt: until ? toIso(date, until) : null,
          purpose,
          roomIds: room ? [room] : [],
          hostUserId: host,
        },
      });
      await refresh();
      toast(
        v.status === "approved" ? "Bezoek aangekondigd en goedgekeurd" : "Bezoek aangekondigd, wacht op de gastheer",
      );
      onSaved(v);
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Bezoek aankondigen"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy || !ok}>
            Aankondigen
          </button>
        </>
      }
    >
      <Combobox
        id="visit-who"
        label="Wie komt er? *"
        items={items}
        value={whoValue}
        placeholder="Zoek een collega of iemand uit het adresboek"
        onChange={(v) =>
          setWho(
            !v
              ? null
              : v.startsWith("u:")
                ? { kind: "member", userId: v.slice(2) }
                : v.startsWith("p:")
                  ? { kind: "person", personId: v.slice(2) }
                  : { kind: "guest" },
          )
        }
        addLabel={(q) => (q ? `Nieuwe gast: ${q}` : "Nieuwe gast")}
        onAdd={(q) => {
          setGuest((g) => ({ ...g, name: q }));
          setWho({ kind: "guest" });
        }}
        hint={
          who?.kind === "member"
            ? "Eigen dienst is meteen goedgekeurd."
            : "Staat iemand nog nergens, typ de naam en kies Nieuwe gast."
        }
      />
      {who?.kind === "guest" && (
        <div className="grid2">
          <label className="field">
            <span>Naam *</span>
            <input value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} maxLength={120} />
          </label>
          <label className="field">
            <span>Bedrijf</span>
            <input
              value={guest.company}
              onChange={(e) => setGuest({ ...guest, company: e.target.value })}
              maxLength={120}
              autoComplete="organization"
            />
          </label>
          <label className="field">
            <span>E-mail</span>
            <input
              type="email"
              value={guest.email}
              onChange={(e) => setGuest({ ...guest, email: e.target.value })}
              autoComplete="email"
            />
          </label>
          <label className="field">
            <span>Telefoon</span>
            <input
              type="tel"
              value={guest.phone}
              onChange={(e) => setGuest({ ...guest, phone: e.target.value })}
              autoComplete="tel"
            />
          </label>
        </div>
      )}
      <div className="grid3">
        <label className="field">
          <span>Dag *</span>
          <input type="date" value={date} min={localDate(now)} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="field">
          <span>Van *</span>
          <input type="time" value={from} step={900} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="field">
          <span>Tot</span>
          <input
            type="time"
            value={until}
            step={900}
            onChange={(e) => setUntil(e.target.value)}
            aria-invalid={badEnd || undefined}
          />
          {badEnd && <span className="hint err">Het einde ligt voor het begin</span>}
        </label>
      </div>
      <label className="field">
        <span>Waarvoor</span>
        <input
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          maxLength={300}
          placeholder="bijv. Onderhoud lift A"
        />
      </label>
      <div className="grid2">
        {settings.roomMode !== "off" && b.rooms.length > 0 && (
          <Combobox
            id="visit-room"
            label="Ruimte"
            items={roomItems(b)}
            value={room}
            onChange={setRoom}
            clearLabel="Nog niet bekend"
            placeholder="Zoek een ruimte"
          />
        )}
        {hostItems.length > 0 && (
          <Combobox
            id="visit-host"
            label="Gastheer"
            items={hostItems}
            value={host}
            onChange={setHost}
            clearLabel="Eerste gastheer die reageert"
            placeholder="Zoek een gastheer"
          />
        )}
      </div>
      {settings.accessNote && <p className="muted small">Toegang: {settings.accessNote}</p>}
    </Modal>
  );
}

/** Binnen melden, met ruimte als het gebouw daarom vraagt. */
export function CheckInDialog({
  building: b,
  visit: v,
  settings,
  onClose,
}: {
  building: BuildingDto;
  visit: VisitDto;
  settings: VisitSettingsDto;
  onClose: () => void;
}) {
  const toast = useToast();
  const refresh = useVisitsRefresh(b.id);
  const moving = v.status === "inside";
  const [room, setRoom] = useState<string | null>(v.room?.id ?? v.rooms[0]?.id ?? null);
  const [busy, setBusy] = useState(false);
  const required = settings.roomMode === "required";

  async function save() {
    setBusy(true);
    try {
      if (moving) await api(`/visits/${v.id}/room`, { method: "POST", body: { roomId: room } });
      else await api(`/visits/${v.id}/check-in`, { method: "POST", body: { roomId: room } });
      await refresh(v);
      toast(moving ? "Ruimte gewijzigd" : `${v.visitor.name} is binnen`);
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={moving ? `Ruimte van ${v.visitor.name}` : `${v.visitor.name} binnen melden`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => void save()}
            disabled={busy || (required && !room)}
          >
            {moving ? "Opslaan" : "Binnen"}
          </button>
        </>
      }
    >
      <Combobox
        id="checkin-room"
        label={required ? "Ruimte *" : "Ruimte"}
        items={roomItems(b)}
        value={room}
        onChange={setRoom}
        clearLabel={required ? undefined : "Geen ruimte"}
        placeholder="Zoek een ruimte"
        hint="Bij een ontruiming zie je waar iemand is."
      />
      {v.status === "announced" && <p className="muted small">Binnen melden telt als goedkeuren.</p>}
    </Modal>
  );
}

/** Uitklokken door gastheer of receptie: altijd met een reden, zodat de lijst Aanwezig te vertrouwen blijft. */
export function CheckOutDialog({
  buildingId,
  visit: v,
  onClose,
}: {
  buildingId: string;
  visit: VisitDto;
  onClose: () => void;
}) {
  const toast = useToast();
  const refresh = useVisitsRefresh(buildingId);
  const [reason, setReason] = useState("Vertrokken");
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await api(`/visits/${v.id}/check-out`, { method: "POST", body: { reason } });
      await refresh(v);
      toast(`${v.visitor.name} is vertrokken`);
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`${v.visitor.name} uitklokken`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy || !reason.trim()}>
            Vertrokken
          </button>
        </>
      }
    >
      <label className="field">
        <span>Reden *</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} list="checkout-reasons" />
        <datalist id="checkout-reasons">
          <option value="Vertrokken" />
          <option value="Vertrokken via receptie" />
          <option value="Gezien bij vertrek" />
          <option value="Telefonisch afgemeld" />
          <option value="Niet meer in het gebouw bij controle" />
        </datalist>
        <span className="hint">Komt in het logboek van het bezoek.</span>
      </label>
    </Modal>
  );
}

/** Afwijzen, afzeggen of niet gekomen, met een optionele notitie. */
export function NoteDialog({
  buildingId,
  visit: v,
  action,
  onClose,
}: {
  buildingId: string;
  visit: VisitDto;
  action: "reject" | "cancel" | "no-show";
  onClose: () => void;
}) {
  const toast = useToast();
  const refresh = useVisitsRefresh(buildingId);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const text = {
    reject: { title: "Bezoek afwijzen", button: "Afwijzen", done: "Bezoek afgewezen", hint: "bijv. Niet afgesproken" },
    cancel: {
      title: "Bezoek afzeggen",
      button: "Afzeggen",
      done: "Bezoek afgezegd",
      hint: "bijv. Verzet naar volgende week",
    },
    "no-show": {
      title: "Niet gekomen",
      button: "Niet gekomen",
      done: "Gemarkeerd als niet gekomen",
      hint: "bijv. Afgebeld",
    },
  }[action];

  async function save() {
    setBusy(true);
    try {
      await api(`/visits/${v.id}/${action}`, { method: "POST", body: { note } });
      await refresh(v);
      toast(text.done);
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={text.title}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Terug
          </button>
          <button
            type="button"
            className={action === "reject" ? "btn danger" : "btn primary"}
            onClick={() => void save()}
            disabled={busy}
          >
            {text.button}
          </button>
        </>
      }
    >
      <p>
        {v.visitor.name}
        {v.visitor.company && ` (${v.visitor.company})`}
      </p>
      <label className="field">
        <span>Notitie</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder={text.hint} />
      </label>
    </Modal>
  );
}
